/**
 * A person's face in a table row.
 *
 * The photo needs the admin bearer token, so it cannot be a plain `<img src>` —
 * it goes through the API client as a blob, cached by TanStack Query so the
 * same face on two pages is fetched once. Only the `thumb` variant is
 * requested: a 20-row page pulling 20 full-size images to draw them at 28px is
 * a lot of bytes for nothing.
 *
 * Falls back to initials, then to an icon. The fallback is not a placeholder to
 * be embarrassed about — most seeded accounts are phone-only signups with no
 * photo and no name, and a row of grey circles reads worse than a row of
 * coloured initials.
 */
import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { use_file_blob } from '@/components/auth-image';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: 'h-7 w-7 text-micro',
  md: 'h-9 w-9 text-xs',
  lg: 'h-14 w-14 text-lg',
  xl: 'h-20 w-20 text-2xl',
} as const;

/**
 * Deterministic tint per person, so the same account is the same colour on
 * every page — a face-substitute that changes colour on each render is worse
 * than no colour at all.
 */
const TINTS = [
  'bg-primary/15 text-primary',
  'bg-info/15 text-info',
  'bg-success/15 text-success',
  'bg-warning/20 text-warning',
  'bg-accent/20 text-accent',
  'bg-secondary text-secondary-foreground',
];

function tint_for(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return TINTS[Math.abs(hash) % TINTS.length]!;
}

function initials_of(name: string | null | undefined): string {
  if (!name) return '';
  const words = name
    .trim()
    .split(/\s+/)
    // Skip anything that doesn't start with a letter, so a display name that
    // begins with a symbol or an emoji doesn't produce a garbled monogram.
    .filter((w) => /^\p{L}/u.test(w));
  if (words.length === 0) return '';
  const first = words[0]![0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function PersonAvatar({
  /** Relative API path to the image, or null when none is set. */
  path,
  name,
  /** Stable value the tint is derived from — an id, normally. */
  seed,
  size = 'sm',
  fallback_icon: FallbackIcon,
  className,
}: {
  path: string | null;
  name?: string | null;
  seed?: string;
  size?: keyof typeof SIZES;
  fallback_icon?: LucideIcon;
  className?: string;
}) {
  const blob_q = use_file_blob(path ? `${path}?variant=thumb` : null);
  const [object_url, set_object_url] = useState<string | null>(null);

  useEffect(() => {
    if (!blob_q.data) return;
    const url = URL.createObjectURL(blob_q.data);
    set_object_url(url);
    return () => URL.revokeObjectURL(url);
  }, [blob_q.data]);

  const initials = initials_of(name);
  const shell = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold',
    SIZES[size],
    className,
  );

  // A photo that exists but failed to load falls through to the initials
  // rather than showing a broken-image glyph — the row still has to identify
  // the person.
  if (object_url && !blob_q.isError) {
    return (
      <img
        src={object_url}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className={cn(shell, 'object-cover')}
      />
    );
  }

  return (
    <span className={cn(shell, tint_for(seed ?? name ?? 'anon'))} aria-hidden="true">
      {initials || (FallbackIcon ? <FallbackIcon className="h-1/2 w-1/2" /> : '?')}
    </span>
  );
}
